import { Component, ViewChild, ElementRef, AfterViewInit, Input, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonDataService } from './Services/commonDataService';
import { NavService } from './Services/nav.service';
import { Global } from './Shared/global';
import { Screen } from './Models/Screen';
import { UntypedFormControl } from '@angular/forms';
import { Organisation } from './Models/Common/Organisation';
import { User } from './Models/FitnessBox/User';
import { AppContext } from './Shared/common';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { ApplicationConfiguration } from './Models/ApplicationConfiguration';
import { CommonSetting } from './Models/Common/CommonSettings';
import { IdleUserService } from './Services/idle-user.service';
import { AuthService } from './Services/auth.service';
import { DOCUMENT } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['app.component.css']
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  org: string = '';
  user: string = '';
  indLoading = false;
  navItems: Screen[] = [];
  displayNavigation: boolean = false;
  navVisibilityFilter = { IsVisible: true };
  currentScreen: Screen = null;
  invalidRedirect: boolean = false;
  isAuthenticated: boolean = false;
  isInitialized: boolean = false;

  mode = new UntypedFormControl('over');
  shouldRun = true;
  @ViewChild('appDrawer', { static: false }) appDrawer: ElementRef;
  isUserIdle: boolean = false;
  visibleNavItems = this.navItems.filter(x => x.IsVisible);

  private destroy$ = new Subject<void>();

  constructor(
    private _service: CommonDataService,
    private navService: NavService,
    private elementRef: ElementRef,
    private appctx: AppContext,
    private router: Router,
    private route: ActivatedRoute,
    private idleUserService: IdleUserService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document
  ) {
    // Get org and user from ASPX attributes (legacy mode)
    this.org = this.elementRef.nativeElement.getAttribute('org') || '';
    this.user = this.elementRef.nativeElement.getAttribute('user') || '';

    // Initialize default empty objects
    this.appctx.CurrentOrg = new Organisation();
    this.appctx.CurrentUser = new User();

    this.processAppConfig();
  }

  ngOnInit(): void {
    debugger;
    this.setupUserContext();
    this.setupIdleUser();
    this.setupRouterSubscription();

    // Check if user is authenticated via AuthService (new Angular auth mode)
    this.isAuthenticated = this.authService.isLoggedIn();
    if (this.isAuthenticated) {
      // Already handled by setupUserContext
    } else if (this.org && this.user) {
      console.warn('[AppComponent] Legacy mode detected - delegating to AuthService');

      try {
        const currentOrg = JSON.parse(this.org.replaceAll("'", "\""));
        const currentUser = JSON.parse(this.user.replaceAll("'", "\""));

        // Pass through AuthService instead of setting globals directly
        this.authService['currentUserSubject'].next(currentUser);
        this.authService['currentOrgSubject'].next(currentOrg);

        this.setupUserContext();
      } catch (e) {
        console.error('Legacy parsing failed:', e);
        this.redirectToLogin();
      }
    } else {
      // Pure Angular mode - no org/user, redirect to login
      console.warn('User not authenticated, redirecting to login');
      this.redirectToLogin();
    }
  }

  ngAfterViewInit(): void {
    this.navService.appDrawer = this.appDrawer;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupUserContext(): void {
    const currentUser = this.authService.getCurrentUser();
    const currentOrg = this.authService.getCurrentOrganization();

    if (currentUser) {
      this.appctx.CurrentUser = currentUser as User;
      Global.CurrentUser = currentUser as User;
    }

    if (currentOrg) {
      this.appctx.CurrentOrg = this.convertAuthOrgToAppOrg(currentOrg);
      Global.CurrentOrg = this.appctx.CurrentOrg;
    }

    this.isInitialized = true;
    this.Load();
    this.displayNavigation = true;
  }

  private setupIdleUser(): void {
    this.idleUserService.userInactive
      .pipe(takeUntil(this.destroy$))
      .subscribe(isIdle => {
        if (isIdle) {
          this.authService.logout().subscribe(() => {
            this.router.navigate(['/login']);
          });
        }
      });
  }

  private setupRouterSubscription(): void {
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (event instanceof NavigationEnd) {
          if (this.navItems && this.navItems.length > 0) {
            this.invalidRedirect = false;
            // Update menu selection based on current route
            this.updateMenuSelection(this.router.url);
          } else {
            this.invalidRedirect = true;
          }
          // Only validate access if we have navigation items
          if (this.isInitialized) {
            this.validateAccess(this.router.url);
          }
        }
      });
  }

  /**
   * TrackBy function for *ngFor to prevent unnecessary re-renders
   * ✅ This is crucial - prevents menu-list-item ngOnInit being called on every route change
   */
  trackByRoutingUrl(index: number, item: Screen): any {
    return item.RoutingUrl || index;
  }

  /**
   * Update menu item selection based on current route
   */
  private updateMenuSelection(url: string): void {
    let normalizedUrl = decodeURIComponent(url).replace(/^\//, '');
    normalizedUrl = normalizedUrl.split('?')[0].split('/edit')[0].split('/upgrade')[0];
    let urlPart = normalizedUrl.split('/')[0];

    // Clear previous selections
    this.clearMenuSelections(this.navItems);

    // Find and mark matching menu item as selected
    this.navItems.forEach(item => {
      if (item.RoutingUrl === normalizedUrl || item.RoutingUrl === urlPart) {
        item.IsSelected = true;
        this.appctx.CurrentScreen = this.currentScreen = item;
      }

      if (item.children && item.children.length > 0) {
        item.children.forEach(child => {
          if (child.RoutingUrl === normalizedUrl || child.RoutingUrl === urlPart) {
            child.IsSelected = true;
            item.IsSelected = true; // Also mark parent as selected
            this.appctx.CurrentScreen = this.currentScreen = child;
          }
        });
      }
    });

    this.cdr.markForCheck();
  }

  /**
   * Clear all menu selections
   */
  private clearMenuSelections(items: Screen[]): void {
    items.forEach(item => {
      item.IsSelected = false;
      if (item.children && item.children.length > 0) {
        this.clearMenuSelections(item.children);
      }
    });
  }

  /**
   * Convert AuthService Organization to AppOrganization format
   */
  private convertAuthOrgToAppOrg(authOrg: Organisation): Organisation {
    const org = new Organisation();
    org.OrgId = authOrg.OrgId;
    org.OrgName = authOrg.OrgName;
    org.IsActive = authOrg.IsActive;
    org.OrgUnits = authOrg.OrgUnits.map(unit => ({
      UnitId: unit.UnitId,
      UnitName: unit.UnitName,
      IsActive: unit.IsActive,
      IsSelected: unit.IsSelected || true
    }));
    return org;
  }

  /**
   * Redirect to login page
   */
  private redirectToLogin(): void {
    this.router.navigate(['/login']);
  }

  processAppConfig(): void {
    this.appctx.AppConfigurations = new ApplicationConfiguration();
    this.appctx.AppConfigurations.Settings = [];
    this.appctx.AppConfigurations.CustomSettings = {};
    this.appctx.AppConfigurations.CustomSettings.MergeFullName = true;
  }

  validateAccess(requestedUrl: string): void {
    let originalUrl = requestedUrl;
    let hasViewAccess: boolean = false;

    // Normalize URL
    requestedUrl = decodeURIComponent(requestedUrl).replace(/^\//, '');
    requestedUrl = requestedUrl.split('?')[0].split('/edit')[0].split('/upgrade')[0];
    let urlPart: string = requestedUrl.split('/')[0];

    // Public routes that don't need access validation
    const publicRoutes = ['restrictedaccess', 'redirector', 'login', 'org-selector'];
    if (publicRoutes.includes(urlPart)) {
      return; // Skip validation
    }

    // Check access for non-public routes
    if (this.navItems && this.navItems.length > 0) {
      this.navItems.forEach(t => {
        if (t.RoutingUrl === requestedUrl || t.RoutingUrl === urlPart) {
          hasViewAccess = true;
          this.appctx.CurrentScreen = this.currentScreen = t;
        }

        if (t.children && t.children.length > 0) {
          t.children.forEach(c => {
            if (c.RoutingUrl === requestedUrl || c.RoutingUrl === urlPart) {
              hasViewAccess = true;
              this.appctx.CurrentScreen = this.currentScreen = c;
            }
          });
        }
      });

      if (!hasViewAccess && !publicRoutes.includes(requestedUrl)) {
        this.router.navigate(['/restrictedaccess']);
      }
    } else if (this.invalidRedirect && !publicRoutes.includes(requestedUrl)) {
      // Redirect through redirector component
      //this.router.navigate(['/redirector'], {
      //  queryParams: { redirectto: originalUrl }
      //});
    }
  }

  public Load(): void {
    debugger;
    if (!this.appctx.CurrentOrg || !this.appctx.CurrentOrg.OrgId ||
      !this.appctx.CurrentUser || !this.appctx.CurrentUser.ID) {
      console.warn('AppComponent.Load(): Organization or User not initialized');
      return;
    }

    this.indLoading = true;
    this.cdr.markForCheck(); // ✅ Trigger change detection

    // Load navigation menu
    this._service
      .getData(Global.BASE_USER_ENDPOINT() + Global.ScreensController + "GetNavigationMenu/?userId=" + this.appctx.CurrentUser.ID + "&filter=")
      .then((x) => {
        this.bindNavigation(<Screen[]>x.ListItems);
        this.cdr.markForCheck(); // ✅ Trigger change detection after binding
      })
      .catch(error => {
        console.error('Error loading navigation:', error);
        this.stopLoading();
      });

    // Load application configuration
    this._service
      .getData(Global.BASE_USER_ENDPOINT() + Global.ApplicationController)
      .then((x) => {
        console.log('processed application updates');
        this.stopLoading();
      })
      .catch(error => {
        console.error('Error loading application config:', error);
        this.stopLoading();
      });
  }

  private bindNavigation(menuItems: Screen[]): void {
    debugger;
    if (!this.navItems.length) {
      this.navItems = menuItems;
    }

    //// ✅ Update array in-place instead of reassigning
    //if (this.navItems.length === 0) {
    //  this.navItems.push(...menuItems);
    //  this.cdr.markForCheck(); // ✅ Trigger change detection after initial load
    //} else {
    //  // If you need to refresh, splice instead of reassign
    //  //this.navItems.splice(0, this.navItems.length, ...menuItems);
    //}

    ////// Update selection after loading navigation
    ////this.updateMenuSelection(this.router.url);
    ////this.navItems = menuItems;
  }

  /**
   * Stop the loading spinner and trigger change detection
   */
  private stopLoading(): void {
    this.indLoading = false;
    this.cdr.markForCheck(); // ✅ Trigger change detection when loading is complete
  }

  reset(): void {
    console.log('Reset idle timer');
    this.isUserIdle = false;
    this.idleUserService.reset();
  }

  onActivate($event: any): void { }
  onDeactivate($event: any): void { }
  onAttach($event: any): void { }
  onDetach($event: any): void { }
}

// PIPE for filter
import { Pipe, PipeTransform } from '@angular/core';
@Pipe({
  name: 'visibilityfilter',
  pure: true  // ✅ Changed to true for better performance
})
export class VisibilityFilterPipe implements PipeTransform {
  transform(items: any[], filter: Object): any {
    if (!items || !filter) {
      return items;
    }
    return items.filter(item => item.IsVisible === true);
  }
}

